import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import SettingsComingSoon from '../components/SettingsComingSoon';
import PageLoadingState from '../components/PageLoadingState';
import { PageHeader } from '../components/primitives';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  TextField,
  Button,
  Divider,
  Grid,
  Alert,
  Avatar,
  Link,
} from '@mui/material';
import LegalFooterLinks from '../components/LegalFooterLinks';
import { Link as RouterLink } from 'react-router-dom';
import {
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Palette as PaletteIcon,
  Security as SecurityIcon,
  IntegrationInstructions as IntegrationIcon,
  Dashboard as DashboardIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const DATA_DELETION_URL = 'https://nexus.digitaldynamicsolution.com/data-deletion.html';

const Settings = () => {
  const { showError } = useToast();
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    company: '',
    phone: '',
  });

  const [, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const updateProfileField = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const userData = await authAPI.getCurrentUser();

        setProfileData({
          firstName: userData.first_name || '',
          lastName: userData.last_name || '',
          username: userData.username || '',
          email: userData.email || '',
          company: userData.company || '',
          phone: userData.phone || '',
        });

        if (userData.profile_image) {
          setImagePreview(userData.profile_image);
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
        showError('Failed to load profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [showError]);

  const handleSaveProfile = async () => {
    try {
      setSaving(true);

      const profileToSave = {
        username: profileData.username,
        email: profileData.email,
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        company: profileData.company,
        phone: profileData.phone,
        profile_image: imagePreview || null,
      };

      await authAPI.updateProfile(profileToSave);
      setEditMode(false);

      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = { ...storedUser, ...profileToSave };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      window.location.reload();
    } catch (error) {
      console.error('Failed to save profile:', error);
      showError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    window.location.reload();
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setImagePreview(null);
  };

  if (loading) {
    return <PageLoadingState minHeight="50vh" message="Loading settings…" />;
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader title="Settings" />

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 64,
              fontSize: '0.875rem',
              fontWeight: 500,
            },
          }}
        >
          <Tab icon={<PersonIcon />} label="Profile" iconPosition="start" />
          <Tab
            icon={<NotificationsIcon />}
            label="Notifications"
            iconPosition="start"
          />
          <Tab icon={<PaletteIcon />} label="Appearance" iconPosition="start" />
          <Tab
            icon={<IntegrationIcon />}
            label="Integrations"
            iconPosition="start"
          />
          <Tab
            icon={<DashboardIcon />}
            label="Dashboard"
            iconPosition="start"
          />
          <Tab icon={<SecurityIcon />} label="Security" iconPosition="start" />
        </Tabs>

        {/* Profile Tab — wired to PUT /auth/me/profile */}
        <Box sx={{ p: 3, display: activeTab === 0 ? 'block' : 'none' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Profile Information
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!editMode ? (
                <Button
                  startIcon={<EditIcon />}
                  variant="outlined"
                  onClick={() => setEditMode(true)}
                >
                  Edit Profile
                </Button>
              ) : (
                <>
                  <Button
                    startIcon={<SaveIcon />}
                    variant="contained"
                    onClick={handleSaveProfile}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    startIcon={<CancelIcon />}
                    variant="outlined"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Profile Picture
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar
                src={imagePreview || null}
                sx={{
                  width: 80,
                  height: 80,
                  fontSize: '2rem',
                  bgcolor: imagePreview ? 'transparent' : 'primary.main',
                  border: '3px solid rgba(88, 166, 255, 0.3)',
                }}
              >
                {profileData.firstName?.charAt(0)?.toUpperCase() ||
                  profileData.username?.charAt(0)?.toUpperCase() ||
                  'U'}
              </Avatar>
              {editMode && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="profile-image-upload"
                    type="file"
                    onChange={handleImageUpload}
                  />
                  <label htmlFor="profile-image-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                    >
                      Upload Image
                    </Button>
                  </label>
                  {imagePreview && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={handleRemoveImage}
                      startIcon={<DeleteIcon />}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={profileData.username}
                onChange={(e) => updateProfileField('username', e.target.value)}
                disabled={!editMode}
                sx={{ mb: 2 }}
                helperText="This will be displayed throughout the application"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={profileData.firstName}
                onChange={(e) =>
                  updateProfileField('firstName', e.target.value)
                }
                disabled={!editMode}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={profileData.lastName}
                onChange={(e) => updateProfileField('lastName', e.target.value)}
                disabled={!editMode}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={profileData.email}
                onChange={(e) => updateProfileField('email', e.target.value)}
                disabled={!editMode}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={profileData.phone}
                onChange={(e) => updateProfileField('phone', e.target.value)}
                disabled={!editMode}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company"
                value={profileData.company}
                onChange={(e) => updateProfileField('company', e.target.value)}
                disabled={!editMode}
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Account deletion
          </Typography>
          <Alert severity="info" icon={<DeleteIcon fontSize="inherit" />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Coming soon — self-service not available yet
            </Typography>
            <Typography variant="body2">
              In-app account closure is not implemented. To request deletion of
              your account and stored data, follow the process on our{' '}
              <Link
                href={DATA_DELETION_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                User Data Deletion
              </Link>{' '}
              page.
            </Typography>
          </Alert>
        </Box>

        {/* Notifications — no notification preferences API yet */}
        <Box sx={{ p: 3, display: activeTab === 1 ? 'block' : 'none' }}>
          <SettingsComingSoon
            title="Notification Preferences"
            reason="Email/push preference toggles are not saved yet. Ops alerts (publish failures, stale competitor scrapes, uptime, ML drift) already appear in Slack and in the in-app notification bell."
          />
        </Box>

        {/* Appearance — app uses a single dark theme today */}
        <Box sx={{ p: 3, display: activeTab === 2 ? 'block' : 'none' }}>
          <SettingsComingSoon
            title="Appearance & Theme"
            reason="User-selectable themes and font size are not available yet. Nexus currently uses one dark theme across the dashboard."
          />
        </Box>

        {/* Integrations — canonical path is per-client */}
        <Box sx={{ p: 3, display: activeTab === 3 ? 'block' : 'none' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            Integrations
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Google Search Console</strong> is linked per workspace (client).
            Open <RouterLink to="/clients">Clients</RouterLink>, click a client to
            view details, then use the &quot;Google Search Console&quot; section to
            connect or disconnect.
          </Alert>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Client-managed connections
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Social platform and search integrations are connected from each
                client&apos;s detail page so credentials and account status stay
                tied to the right workspace.
              </Typography>
              <Button component={RouterLink} to="/clients" variant="contained">
                Manage Client Integrations
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Dashboard preferences — not persisted */}
        <Box sx={{ p: 3, display: activeTab === 4 ? 'block' : 'none' }}>
          <SettingsComingSoon
            title="Dashboard Configuration"
            reason="Layout, auto-refresh, and widget toggles are not saved yet. The dashboard uses the default layout for all users."
          />
        </Box>

        {/* Security — no password change / 2FA / session APIs yet */}
        <Box sx={{ p: 3, display: activeTab === 5 ? 'block' : 'none' }}>
          <SettingsComingSoon
            title="Security Settings"
            reason={
              <>
                Password change, two-factor authentication, and &quot;sign out all
                devices&quot; are not available in Settings yet. To sign out on this
                browser, use <strong>Logout</strong> in the sidebar menu.
              </>
            }
          />
        </Box>
      </Paper>

      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
          Legal
        </Typography>
        <LegalFooterLinks sx={{ justifyContent: 'flex-start', '& a': { color: 'primary.main' } }} />
      </Box>
    </Box>
  );
};

export default Settings;
